import { Type, type Static } from '@sinclair/typebox';
import { StringEnum } from '../../utils/typebox';

const BaseAttachmentSchema = {
  object: Type.Literal('Attachment'),
  id: Type.String({ description: 'The unique identifier of the attachment.' }),
  file_size: Type.Optional(Type.Number({ description: 'The size of the attachment in bytes.' })),
  is_inline: Type.Boolean({ description: 'Is the attachment inline in the content.' }),
  content_id: Type.Optional(
    Type.String({
      description:
        'Reserved for email attachments. The Content-ID (CID) used to reference the attachment inline in the email HTML body via `cid:`. Provided without the surrounding angle brackets.',
    }),
  ),
  is_unavailable: Type.Optional(
    Type.Boolean({ description: 'The attachment is not available for download because it was removed from provider servers.' }),
  ),
  mimetype: Type.String({ description: 'The MIME type of the attachment.' }),
  url: Type.String({ description: 'The URL to download the attachment.' }),
  url_expires_at: Type.Optional(
    Type.String({ description: 'The URL expiration timestamp. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).' }),
  ),
  content: Type.Optional(
    Type.String({ description: 'The file content encoded as base64, when available directly without an additional download.' }),
  ),
};

const AudioAttachmentSchema = Type.Object(
  {
    ...BaseAttachmentSchema,
    type: Type.Literal('audio'),
    duration: Type.Optional(Type.Number({ description: 'The duration of the audio in seconds.' })),
    voice_note: Type.Boolean({ description: 'The audio is a voice note (should be displayed differently)' }),
  },
  { title: 'Audio' },
);

const ImageAttachmentSchema = Type.Object(
  {
    ...BaseAttachmentSchema,
    type: Type.Literal('img'),
    size: Type.Optional(
      Type.Object(
        {
          height: Type.Number(),
          width: Type.Number(),
        },
        { description: 'The size of the image in pixels.' },
      ),
    ),
    sticker: Type.Boolean({ description: 'The image is a sticker (should be displayed differently)' }),
  },
  { title: 'Image' },
);

const VideoAttachmentSchema = Type.Object(
  {
    ...BaseAttachmentSchema,
    type: Type.Literal('video'),
    duration: Type.Optional(Type.Number({ description: 'The duration of the video in seconds.' })),
    size: Type.Object(
      {
        height: Type.Number(),
        width: Type.Number(),
      },
      { description: 'The size of the video in pixels.' },
    ),
    gif: Type.Boolean({ description: 'The video is a GIF (should be displayed differently)' }),
  },
  { title: 'Video' },
);

const FileAttachmentSchema = Type.Object(
  {
    ...BaseAttachmentSchema,
    type: Type.Literal('file'),
    filename: Type.String({ description: 'The name of the file, including the extension.' }),
  },
  { title: 'File / Document' },
);

const SharedContentAttachmentSchema = Type.Object(
  {
    ...BaseAttachmentSchema,
    type: Type.Literal('shared_content'),
    media_type: StringEnum(['post', 'story', 'url', 'reel', 'job_posting'], { description: 'The type of content being shared.' }),
    url: Type.String({ description: 'The URL of the shared content.' }),
    author: Type.Optional(Type.String({ description: 'The author of the shared content, if applicable.' })),
    description: Type.Optional(Type.String({ description: 'A brief description of the shared content.' })),
  },
  { title: 'Media Share' },
);

const ContactCardAttachmentSchema = Type.Object(
  {
    ...BaseAttachmentSchema,
    type: Type.Literal('contact_card'),
    display_name: Type.Optional(Type.String({ description: 'The name of the shared contact.' })),
    organization: Type.Optional(Type.String({ description: 'The organization of the shared contact.' })),
    phones: Type.Array(
      Type.Object({
        number: Type.String({ description: 'The phone number of the shared contact.' }),
        type: Type.Optional(
          Type.String({ description: 'The label of the phone number, as set by the contact owner (CELL, WORK, ...).' }),
        ),
      }),
      { description: 'The phone numbers of the shared contact.' },
    ),
    emails: Type.Array(
      Type.Object({
        address: Type.String({ description: 'The email address of the shared contact.' }),
        type: Type.Optional(
          Type.String({ description: 'The label of the email address, as set by the contact owner (HOME, WORK, ...).' }),
        ),
      }),
      { description: 'The email addresses of the shared contact.' },
    ),
  },
  { title: 'Contact Card' },
);

export const AttachmentSchema = Type.Union([
  AudioAttachmentSchema,
  ImageAttachmentSchema,
  VideoAttachmentSchema,
  FileAttachmentSchema,
  SharedContentAttachmentSchema,
  ContactCardAttachmentSchema,
]);

export type Attachment = Static<typeof AttachmentSchema>;

export type AudioAttachment = Static<typeof AudioAttachmentSchema>;
export type ImageAttachment = Static<typeof ImageAttachmentSchema>;
export type VideoAttachment = Static<typeof VideoAttachmentSchema>;
export type FileAttachment = Static<typeof FileAttachmentSchema>;
export type SharedContentAttachment = Static<typeof SharedContentAttachmentSchema>;
export type ContactCardAttachment = Static<typeof ContactCardAttachmentSchema>;
